import { ModalDialog } from '../../lib/ModalDialog'

/**
 * Settings dialog for mocap configuration.
 *
 * Renders a grouped list of per-bone enable checkboxes (Body / Arms / Legs).
 * Selection persists to localStorage. Toggling a checkbox calls back into the
 * consumer to update the live PoseToRig state.
 */

const STORAGE_KEY = 'meshinator-mocap-disabled-bones'
const ROOT_MOTION_STORAGE_KEY = 'meshinator-mocap-root-motion-enabled'
const SMOOTHING_STORAGE_KEY = 'meshinator-mocap-smoothing-strength'
const HANDS_ENABLED_STORAGE_KEY = 'meshinator-mocap-hands-enabled'
const DEFAULT_SMOOTHING_STRENGTH = 0
const MAX_SMOOTHING_STRENGTH = 4

/**
 * Friendlier display name for a bone. Falls back to the raw name if unknown.
 * Order/grouping in the dialog is determined by `BONE_GROUPS` below, not by this map.
 */
const BONE_DISPLAY_NAMES: Record<string, string> = {
  spine_01: 'Spine (lumbar)',
  spine_02: 'Spine (mid)',
  spine_03: 'Spine (upper)',
  neck_01: 'Neck',
  head: 'Head',
  clavicle_l: 'Left clavicle',
  clavicle_r: 'Right clavicle',
  upperarm_l: 'Left upper arm',
  upperarm_r: 'Right upper arm',
  lowerarm_l: 'Left forearm',
  lowerarm_r: 'Right forearm',
  hand_l: 'Left hand',
  hand_r: 'Right hand',
  thigh_l: 'Left thigh',
  thigh_r: 'Right thigh',
  calf_l: 'Left calf',
  calf_r: 'Right calf',
  foot_l: 'Left foot',
  foot_r: 'Right foot'
}

/**
 * Group definition. Bones not in any group fall into "Other" at the bottom.
 * Each group's bones are rendered in the listed order.
 */
const BONE_GROUPS: Array<{ title: string, bones: string[] }> = [
  { title: 'Torso & head', bones: ['spine_01', 'spine_02', 'spine_03', 'neck_01', 'head'] },
  { title: 'Arms', bones: ['clavicle_l', 'upperarm_l', 'lowerarm_l', 'hand_l', 'clavicle_r', 'upperarm_r', 'lowerarm_r', 'hand_r'] },
  { title: 'Legs', bones: ['thigh_l', 'calf_l', 'foot_l', 'thigh_r', 'calf_r', 'foot_r'] }
]

export interface MocapBoneSettingsDeps {
  /** All bones the live PoseToRig knows how to drive (from `get_drivable_bone_names()`). */
  drivable_bones: string[]
  /** Currently disabled bone names (from `get_disabled_bones()`). */
  disabled_bones: string[]
  /** Called when the user toggles a per-bone checkbox. */
  on_toggle: (bone_name: string, enabled: boolean) => void
  /** Current root-motion state (from `PoseToRig.is_root_motion_enabled()`). */
  root_motion_enabled: boolean
  /** Called when the user toggles the root-motion checkbox. */
  on_root_motion_toggle: (enabled: boolean) => void
  /** Current confidence-weighted smoothing strength (0..MAX). */
  smoothing_strength: number
  /** Called when the user moves the smoothing strength slider. */
  on_smoothing_strength_change: (strength: number) => void
  /** Called when the user picks a video file via the "Process video file" button. */
  on_process_video_file?: (file: File) => void
  /** Called when the user clicks "Apply foot lock". */
  on_apply_foot_lock?: () => void
  /** Current "drive hands from webcam" toggle state. */
  hands_enabled: boolean
  /** Called when the user toggles the hand-mocap checkbox. The consumer is
   *  responsible for persisting + asking the live MocapController to enable
   *  or disable its hand provider. */
  on_hands_toggle: (enabled: boolean) => void
}

export class MocapBoneSettings {
  /** Open the settings dialog with the current state. */
  static show (deps: MocapBoneSettingsDeps): void {
    const drivable_set = new Set(deps.drivable_bones)
    const disabled_set = new Set(deps.disabled_bones)

    // Pre-compute which group each drivable bone belongs to, then derive the
    // "Other" bucket = drivable - (everything in any group).
    const grouped: typeof BONE_GROUPS = BONE_GROUPS.map(g => ({
      title: g.title,
      bones: g.bones.filter(b => drivable_set.has(b))
    })).filter(g => g.bones.length > 0)
    const grouped_set = new Set(BONE_GROUPS.flatMap(g => g.bones))
    const other_bones = deps.drivable_bones.filter(b => !grouped_set.has(b))
    if (other_bones.length > 0) grouped.push({ title: 'Other', bones: other_bones })

    const root_motion_checked = deps.root_motion_enabled ? 'checked' : ''
    const smoothing_value = Math.max(0, Math.min(MAX_SMOOTHING_STRENGTH, deps.smoothing_strength))
    let html = '<p>Pick which body parts the webcam drives. Disabled bones return to their rest pose.</p>'
    const hands_checked = deps.hands_enabled ? 'checked' : ''
    html += `<fieldset class="mocap-bone-group"><legend>Hands &amp; fingers</legend>
      <label class="mocap-bone-toggle">
        <input type="checkbox" data-mocap-hands ${hands_checked} />
        <span>Drive fingers from webcam</span>
      </label>
      <p class="mocap-root-motion-hint">Loads MediaPipe HandLandmarker on first activation (~5MB extra download). Drives <code>thumb_*</code>, <code>index_*</code>, <code>middle_*</code>, <code>ring_*</code>, <code>pinky_*</code> bones (<code>_l</code>/<code>_r</code>). Rigs without these finger bones (Tripo, single-hand variants) silently skip them.</p>
    </fieldset>`
    html += `<fieldset class="mocap-bone-group"><legend>Smoothing</legend>
      <label class="mocap-bone-toggle" style="display: flex; flex-direction: row; align-items: center; gap: 0.5rem;">
        <span style="min-width: 110px;">Strength</span>
        <input type="range" data-mocap-smoothing min="0" max="${MAX_SMOOTHING_STRENGTH}" step="0.1" value="${smoothing_value}" style="flex: 1;" />
        <span data-mocap-smoothing-readout style="min-width: 36px; text-align: right; font-variant-numeric: tabular-nums;">${smoothing_value.toFixed(1)}</span>
      </label>
      <p class="mocap-root-motion-hint">Higher values dampen low-confidence frames more — useful when the camera angle is poor or limbs go partially out of frame. 0 disables the dampening (default).</p>
    </fieldset>`
    html += `<fieldset class="mocap-bone-group"><legend>Root motion</legend>
      <label class="mocap-bone-toggle">
        <input type="checkbox" data-mocap-root-motion ${root_motion_checked} />
        <span>Capture XZ translation (hip)</span>
      </label>
      <p class="mocap-root-motion-hint">When on, the rig translates horizontally as you move in front of the camera. Vertical (Y) stays planted because monocular tracking can't reliably distinguish a jump from leaning.</p>
    </fieldset>`
    for (const group of grouped) {
      html += `<fieldset class="mocap-bone-group"><legend>${group.title}</legend>`
      for (const bone of group.bones) {
        const checked = disabled_set.has(bone) ? '' : 'checked'
        const display = BONE_DISPLAY_NAMES[bone] ?? bone
        html += `<label class="mocap-bone-toggle">
          <input type="checkbox" data-bone-name="${bone}" ${checked} />
          <span>${display}</span>
        </label>`
      }
      html += '</fieldset>'
    }
    html += '<div class="mocap-bone-actions"><button type="button" data-mocap-bone-action="enable-all">Enable all</button><button type="button" data-mocap-bone-action="disable-all">Disable all</button></div>'

    if (deps.on_process_video_file !== undefined || deps.on_apply_foot_lock !== undefined) {
      html += '<fieldset class="mocap-bone-group"><legend>Offline mocap</legend>'
      if (deps.on_process_video_file !== undefined) {
        html += `<p class="mocap-root-motion-hint">Process a pre-recorded video file (mp4, webm, mov) frame by frame and bake the result into a new clip. Faster than real-time on most GPUs.</p>
          <button type="button" data-mocap-process-video>Process video file…</button>
          <input type="file" accept="video/*" data-mocap-video-input style="display: none;" />`
      }
      if (deps.on_apply_foot_lock !== undefined) {
        html += `<p class="mocap-root-motion-hint" style="margin-top: 0.75rem;">Apply foot-locking to the active recorded clip. Detects when feet are planted on the ground and uses IK on the leg chain to keep the foot from sliding while the body moves.</p>
          <button type="button" data-mocap-apply-foot-lock>Apply foot lock to active clip</button>`
      }
      html += '</fieldset>'
    }

    const dialog = new ModalDialog('Mocap settings', html, { customClass: 'mocap-bone-dialog' })
    dialog.show()

    // Wire checkbox + bulk-action handlers via event delegation on the
    // dialog's content. The content was just appended to document.body by
    // ModalDialog.show(), so query is safe.
    const overlay = document.querySelector('.modal-dialog-overlay.mocap-bone-dialog')
    overlay?.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement
      if (target?.type !== 'checkbox') return

      // Root-motion toggle uses its own data attribute so it doesn't get caught
      // by the bone-name handler.
      if (target.hasAttribute('data-mocap-root-motion')) {
        deps.on_root_motion_toggle(target.checked)
        return
      }

      if (target.hasAttribute('data-mocap-hands')) {
        deps.on_hands_toggle(target.checked)
        return
      }

      const bone_name = target.dataset.boneName
      if (bone_name === undefined) return
      deps.on_toggle(bone_name, target.checked)
    })

    // Smoothing slider — fires `input` continuously while dragging so the live
    // PoseToRig responds in real time.
    overlay?.addEventListener('input', (event) => {
      const target = event.target as HTMLInputElement
      if (!target.hasAttribute('data-mocap-smoothing')) return
      const value = parseFloat(target.value)
      if (!Number.isFinite(value)) return
      const readout = overlay.querySelector('[data-mocap-smoothing-readout]')
      if (readout !== null) readout.textContent = value.toFixed(1)
      deps.on_smoothing_strength_change(value)
    })
    overlay?.addEventListener('click', (event) => {
      const target = event.target as HTMLButtonElement
      const action = target?.dataset?.mocapBoneAction
      if (action === undefined) return
      const enable_all = action === 'enable-all'
      // Update every checkbox + fire the consumer callback for each toggle.
      const checkboxes = overlay.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-bone-name]')
      checkboxes.forEach((cb) => {
        if (cb.checked === enable_all) return // no change
        cb.checked = enable_all
        const name = cb.dataset.boneName
        if (name !== undefined) deps.on_toggle(name, enable_all)
      })
    })

    // "Process video file" button → trigger hidden file input → on file
    // selected, fire the consumer callback. The settings dialog stays open;
    // the callback decides what to do (typically: close the dialog and show
    // a progress modal).
    const video_button = overlay?.querySelector<HTMLButtonElement>('[data-mocap-process-video]')
    const video_input = overlay?.querySelector<HTMLInputElement>('[data-mocap-video-input]')
    video_button?.addEventListener('click', () => video_input?.click())
    video_input?.addEventListener('change', () => {
      const file = video_input.files?.[0]
      if (file === undefined || deps.on_process_video_file === undefined) return
      deps.on_process_video_file(file)
    })

    // "Apply foot lock" button.
    const foot_lock_button = overlay?.querySelector<HTMLButtonElement>('[data-mocap-apply-foot-lock]')
    foot_lock_button?.addEventListener('click', () => {
      if (deps.on_apply_foot_lock !== undefined) deps.on_apply_foot_lock()
    })
  }

  // -- Persistence helpers (consumer calls these) --

  static load_disabled_bones (): string[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw === null) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
    } catch (_) { return [] }
  }

  static save_disabled_bones (names: string[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(names))
    } catch (_) { /* private mode, quota — non-fatal */ }
  }

  static load_root_motion_enabled (): boolean {
    try {
      const raw = localStorage.getItem(ROOT_MOTION_STORAGE_KEY)
      return raw === '1'
    } catch (_) { return false }
  }

  static save_root_motion_enabled (enabled: boolean): void {
    try {
      localStorage.setItem(ROOT_MOTION_STORAGE_KEY, enabled ? '1' : '0')
    } catch (_) { /* non-fatal */ }
  }

  static load_smoothing_strength (): number {
    try {
      const raw = localStorage.getItem(SMOOTHING_STORAGE_KEY)
      if (raw === null) return DEFAULT_SMOOTHING_STRENGTH
      const parsed = parseFloat(raw)
      if (!Number.isFinite(parsed)) return DEFAULT_SMOOTHING_STRENGTH
      return Math.max(0, Math.min(MAX_SMOOTHING_STRENGTH, parsed))
    } catch (_) { return DEFAULT_SMOOTHING_STRENGTH }
  }

  static save_smoothing_strength (strength: number): void {
    try {
      const clamped = Math.max(0, Math.min(MAX_SMOOTHING_STRENGTH, strength))
      localStorage.setItem(SMOOTHING_STORAGE_KEY, clamped.toString())
    } catch (_) { /* non-fatal */ }
  }

  static load_hands_enabled (): boolean {
    try {
      const raw = localStorage.getItem(HANDS_ENABLED_STORAGE_KEY)
      return raw === '1'
    } catch (_) { return false }
  }

  static save_hands_enabled (enabled: boolean): void {
    try {
      localStorage.setItem(HANDS_ENABLED_STORAGE_KEY, enabled ? '1' : '0')
    } catch (_) { /* non-fatal */ }
  }
}
